import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { IconCircle } from '@tabler/icons-react';

import { cn } from '../../lib/utils';
import { Label } from '../Label';
import type { RadioGroupProps, RadioGroupItemProps } from './types';

const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, children, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn('eye:grid eye:gap-2xs', className)}
    {...props}
  >
    {children}
  </RadioGroupPrimitive.Root>
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, label, id, value, ...props }, ref) => {
  const generatedId = React.useId();
  const itemId = id ?? generatedId;

  return (
    <div
      className={cn(
        'eye:flex eye:cursor-pointer eye:items-center eye:gap-2xs',
        'eye:has-[button:disabled]:cursor-not-allowed eye:has-[button:disabled]:[&_[data-slot=label]]:cursor-not-allowed',
      )}
    >
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          'eye:focus-visible:ring-ring eye:aspect-square eye:h-20 eye:w-20 eye:cursor-pointer eye:rounded-full eye:border eye:border-black eye:text-black eye:focus:outline-none eye:focus-visible:ring-1 eye:disabled:cursor-not-allowed eye:disabled:opacity-50',
          className,
        )}
        id={itemId}
        value={value}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className='eye:flex eye:items-center eye:justify-center'>
          <IconCircle className='eye:h-16 eye:w-16 eye:fill-black' />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && (
        <Label className='eye:cursor-pointer' htmlFor={itemId}>
          {label}
        </Label>
      )}
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
